import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AxiosError } from 'axios';
import { get, isEqual } from 'lodash';

import { PayloadApiSuccess } from '../actions/sagaApi';
import { DataPaginated } from '../reducers/createResultReducer';
import { ReturnTypeFetch, ReturnTypeFetchList, ReturnTypeFetchPage } from '../resources/createResourceCommonActions';
import { EntityBase } from '../resources/types';

import usePrevious from './usePrevious';
import useSelectEntitiesDataByResult from './useSelectEntitiesDataByResult';

export interface UseQueryOptions {
  skip?: boolean;
  // TODO add more options in case we need them
  // https://www.apollographql.com/docs/react/api/react-hooks/#options
  // https://relay.dev/docs/en/experimental/api-reference#arguments-1
}

export interface UseQueryResponse<E extends EntityBase> {
  response?: PayloadApiSuccess<E>;
  error?: AxiosError;
  refetch: () => void;
  loading: boolean;
  // TODO add more result values in case we need them
  // https://www.apollographql.com/docs/react/api/react-hooks/#result
  // https://relay.dev/docs/en/experimental/api-reference#return-value-1
}

/**
 * Hook for `resource.fetch` and `resource.fetchList` actions
 *
 * - https://www.apollographql.com/docs/react/api/react-hooks/#usequery
 * - https://relay.dev/docs/en/experimental/api-reference#uselazyloadquery
 *
 * TODO: Support `resource.fetchPage` actions
 *
 * TODO: Create `useMutation` in case we need it
 * - https://www.apollographql.com/docs/react/api/react-hooks/#usemutation
 *
 * NOTE: Using Function Overloading to return different `data` type depending on the action
 * - https://github.com/microsoft/TypeScript/issues/24929#issuecomment-428333515
 *
 * @param action the object returned by calling `resource.fetch` or `resource.fetchList`
 * @param options https://www.apollographql.com/docs/react/api/react-hooks/#options
 *
 * @returns https://www.apollographql.com/docs/react/api/react-hooks/#result
 *
 * @example
 *
 * import React from 'react';
 * import { useQuery } from 'lib/hooks';
 * import { usersResource } from 'lib/resources';
 *
 * const { data, loading, error } = useQuery(usersResource.fetch({ id }));
 */
// eslint-disable-next-line
function useQuery<E extends EntityBase>(
  action: ReturnTypeFetch<E>,
  options?: UseQueryOptions,
): UseQueryResponse<E> & { data?: E };

// eslint-disable-next-line
function useQuery<E extends EntityBase>(
  action: ReturnTypeFetchList<E>,
  options?: UseQueryOptions,
): UseQueryResponse<E> & { data?: E[] };

// eslint-disable-next-line
function useQuery<E extends EntityBase>(
  action: ReturnTypeFetchPage<E>,
  options?: UseQueryOptions,
): UseQueryResponse<E> & { data?: DataPaginated<E> };

// eslint-disable-next-line
function useQuery(action: any, options: UseQueryOptions = {}) {
  const dispatch = useDispatch();

  const [refetchCount, setRefetchCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<PayloadApiSuccess<any>>();
  const [error, setError] = useState<AxiosError>();

  const prevAction = usePrevious(action);
  const prevRefetchCount = usePrevious(refetchCount);
  const { skip } = options;

  const data = useSelectEntitiesDataByResult({
    resource: action.payload.resource,
    result: get(response, 'normalized.result'),
  });

  useEffect(() => {
    if (skip || (isEqual(action, prevAction) && isEqual(refetchCount, prevRefetchCount))) {
      return;
    }
    setLoading(true);
    setError(undefined);
    const meta = action.payload.meta || {};
    dispatch({
      ...action,
      payload: {
        ...action.payload,
        meta: {
          ...meta,
          callbackSuccess: (res: PayloadApiSuccess<any>) => {
            setLoading(false);
            setResponse(res);
            if (meta.callbackSuccess) {
              meta.callbackSuccess(res);
            }
          },
          callbackError: (res?: AxiosError) => {
            setLoading(false);
            setError(res);
            if (meta.callbackError) {
              meta.callbackError(res);
            }
          },
        },
      },
    });
  }, [dispatch, action, prevAction, refetchCount, prevRefetchCount, skip]);

  const refetch = useCallback(() => setRefetchCount(refetchCount + 1), [refetchCount, setRefetchCount]);

  // NOTE: selector data will return one cicle after `loading` is set to `false`
  return { response, data, error, refetch, loading: loading || !data };
}

export default useQuery;
